// 📱 FULL MOBILE RESPONSIVE WORKING FILE

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  TextField,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Backdrop,
  useMediaQuery,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  where,
  getDocs,
  orderBy,
  writeBatch,
  getDoc,
} from "firebase/firestore";

import * as XLSX from "xlsx";
import { db } from "../../firebase";

function ContactDivision() {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [divisions, setDivisions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [openContacts, setOpenContacts] = useState(false);

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ division: "" });
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ division: "" });
  };

  useEffect(() => {
    // Create a query that targets the collection and applies the sort
    const q = query(collection(db, "bdivisions"), orderBy("division", "asc"));

    const unsub = onSnapshot(q, (snap) => {
      setDivisions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, []);

  // Fetch contacts
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "contacts"), (snap) => {
      setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const handleSubmit = async () => {
    if (!form.division.trim()) {
      alert("Enter city name");
      return;
    }

    if (editId) {
      await updateDoc(doc(db, "bdivisions", editId), form);
    } else {
      await addDoc(collection(db, "bdivisions"), form);
    }

    handleClose();
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({ division: item.division });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "bdivisions", id));
  };

  const handleExcelUpload = async (e, divisionId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setProgress({ current: 0, total: 0 });

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(sheet);

      // --- STEP 1: Filter Duplicates in the Excel file itself ---
      const uniqueMap = new Map();
      rawRows.forEach((row) => {
        let mobile = String(row.mobile || row.Mobile || "").trim();
        if (mobile && mobile.length >= 10) {
          // Key by mobile to ensure the Excel file doesn't have internal duplicates
          uniqueMap.set(mobile, {
            parentName: row.Name || row.name || "Unknown",
            mobile: mobile,
            whatsapp: mobile,
            divisionId: divisionId,
            status: "Active",
            created_at: new Date(),
          });
        }
      });

      const uniqueRows = Array.from(uniqueMap.values());
      setProgress({ current: 0, total: uniqueRows.length });

      // --- STEP 2: Batch Upload to Firestore ---
      const chunkSize = 500;
      for (let i = 0; i < uniqueRows.length; i += chunkSize) {
        const chunk = uniqueRows.slice(i, i + chunkSize);
        const batch = writeBatch(db);

        chunk.forEach((data) => {
          // 🔑 CRITICAL CHANGE: Unique ID per Division + Mobile
          // This keeps one record per number per division.
          const customId = `${divisionId}_${data.mobile}`;
          const docRef = doc(db, "contacts", customId);

          // Using .set() without merge will refresh the data if it exists,
          // effectively keeping only one "clean" version of the contact.
          batch.set(docRef, data);
        });

        await batch.commit();
        setProgress((prev) => ({ ...prev, current: i + chunk.length }));
      }

      alert(`Success! Contacts processed for this division.`);
    } catch (err) {
      console.error("Upload Error:", err);
      alert("Error importing Excel.");
    }

    setLoading(false);
    e.target.value = null;
  };

  const handleVCFUpload = async (e, divisionId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);

    try {
      const text = await file.text();
      const entries = text.split("BEGIN:VCARD");
      let imported = 0;

      for (let i = 1; i < entries.length; i++) {
        const card = entries[i];

        const nameMatch = card.match(/FN:(.+)/);
        const mobileMatch = card.match(/TEL.*:(.+)/);

        const parentName = nameMatch ? nameMatch[1].trim() : "";
        const mobile = mobileMatch ? mobileMatch[1].replace(/\D/g, "") : "";

        if (!mobile) continue;

        const qSnap = await getDocs(
          query(collection(db, "contacts"), where("mobile", "==", mobile))
        );
        qSnap.forEach(async (d) => await deleteDoc(doc(db, "contacts", d.id)));

        await addDoc(collection(db, "contacts"), {
          parentName,
          mobile,
          whatsapp: mobile,
          divisionId,
          status: "Active",
          created_at: new Date(),
        });

        imported++;
      }

      alert(`Imported ${imported} VCF contacts.`);
    } catch (err) {
      alert("Failed to import VCF.");
    }

    setLoading(false);
    e.target.value = null;
  };

  const handleViewContacts = (divisionId) => {
    const list = contacts.filter((c) => c.divisionId === divisionId);
    setSelectedContacts(list);
    setOpenContacts(true);
  };

  return (
    <Box p={isMobile ? 1 : 1}>
      <Typography variant="h5" mb={2} textAlign={isMobile ? "center" : "left"}>
        Divisions
      </Typography>

      {/* Add Button responsive */}
      <Box textAlign={isMobile ? "center" : "left"} mb={2}>
        <Button variant="contained" size={isMobile ? "small" : "medium"} onClick={handleOpen}>
          Add Division
        </Button>
      </Box>

      {/* Responsive Table Wrapper */}
      <Paper sx={{ width: "100%", overflowX: "auto" }}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Sr</TableCell>
              <TableCell>Division</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Excel</TableCell>
              <TableCell>VCF</TableCell>
              {/* <TableCell>View</TableCell> */}
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {divisions.map((item, index) => {
              const total = contacts.filter((c) => c.divisionId === item.id).length;

              return (
                <TableRow key={item.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.division}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size={isMobile ? "small" : "medium"}
                      onClick={() => handleViewContacts(item.id)}
                    >
                      <span>{total}</span>
                    </Button>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      component="label"
                    >
                      <UploadFileIcon />
                      {!isMobile && "Excel"}
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        hidden
                        onChange={(e) => handleExcelUpload(e, item.id)}
                      />
                    </Button>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      component="label"
                    >
                      <UploadFileIcon />
                      {!isMobile && "VCF"}
                      <input
                        type="file"
                        accept=".vcf"
                        hidden
                        onChange={(e) => handleVCFUpload(e, item.id)}
                      />
                    </Button>
                  </TableCell>

                  {/* <TableCell>
                    <Button
                      variant="contained"
                      size={isMobile ? "small" : "medium"}
                      startIcon={!isMobile && <VisibilityIcon />}
                      onClick={() => handleViewContacts(item.id)}
                    >
                      {isMobile ? <VisibilityIcon /> : "View"}
                    </Button>
                  </TableCell> */}

                  <TableCell>
                    <IconButton size={isMobile ? "small" : "medium"} onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>

                    <IconButton size={isMobile ? "small" : "medium"} color="error" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Loading Overlay */}
      {/* Loading Overlay with Counter */}
      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: 10000, flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress size={70} thickness={4} color="inherit" />
        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            Importing Contacts...
          </Typography>
          <Typography variant="h4" sx={{ my: 1 }}>
            {progress.current} / {progress.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please do not refresh the page
          </Typography>
        </Paper>
      </Backdrop>

      {/* Modal: Add / Edit City */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            width: isMobile ? "90%" : 400,
            mx: "auto",
            mt: 10,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2} textAlign="center">
            {editId ? "Update Division" : "Add Division"}
          </Typography>

          <TextField
            label="Division Name"
            fullWidth
            value={form.division}
            onChange={(e) => setForm({ division: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Button variant="contained" fullWidth onClick={handleSubmit}>
            {editId ? "Update" : "Save"}
          </Button>
        </Box>
      </Modal>

      {/* Modal: Contacts List */}
      <Modal open={openContacts} onClose={() => setOpenContacts(false)}>
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            width: isMobile ? "90%" : 600,
            mx: "auto",
            mt: 8,
            borderRadius: 2,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" mb={2} textAlign="center">
            Contacts List
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sr</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {selectedContacts.map((c, i) => (
                <TableRow key={c.id}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{c.parentName}</TableCell>
                  <TableCell>{c.mobile}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Button variant="contained" sx={{ mt: 2 }} fullWidth onClick={() => setOpenContacts(false)}>
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}

export default ContactDivision;
